import React, { useState } from 'react';
import { Text, SizeableRoundedDialog, PrimaryButton, Colors, SecondaryButton } from 'library';
import { Grid } from '@mui/material';
import { client, consoleLogInDev, useClient } from 'shared';
import { ShiftProviderUser } from 'models';
import RevealModal from '../ShiftCard/RevealModal';

export default function ComplianceActionInfoModal({
    modalOpen,
    setModalOpen,
    actionText,
    provider_user,
    business_location_id,
    refresh,
}: {
    modalOpen: boolean;
    setModalOpen: (open: boolean) => void;
    actionText: string;
    provider_user: ShiftProviderUser;
    business_location_id: number;
    refresh: () => void;
}) {
    const [revealOpen, setRevealOpen] = useState(false);
    const { loading, send } = useClient({
        url: 'api/compliance-action/initiate/',
        options: { sendImmediately: false },
    });
    const actionType = actionText.toLowerCase().replace(' ', '_');
    function onConfirm() {
        const body = {
            action: actionType,
            provider_user_id: provider_user?.id,
            business_location_id: business_location_id,
        };
        send({ method: 'POST', body })
            .then(() => {
                refresh();
            })
            .catch((e) => {
                alert('An error occurred. Please try again. If this persists please reach out to your ');
                consoleLogInDev(e);
            })
            .finally(() => setModalOpen(false));
    }

    const onSendCode = () => {
        try {
            setRevealOpen(true);
            client(`api/business/reveal-code/`, { method: 'POST', body: { provider: provider_user?.id } });
        } catch (error) {
            alert('Cannot send SSN reveal code');
        }
    };

    const ResubmissionContent = () => {
        return (
            <Grid item container style={{ padding: 20, gap: 20 }}>
                <Text variant="h1">{`Action Required: Complete Resubmission`}</Text>
                <Text>
                    {
                        "The worker you've booked has successfully passed their background check and met all other state requirements. However, due to a lapse of over 90 days since their last employment at a child care facility, a resubmission in Florida's Clearinghouse is necessary before they can start."
                    }
                </Text>
                <Text bold>
                    {'Please follow these steps promptly, as resubmissions can take a few days to process:'}
                </Text>
                <Grid
                    item
                    container
                    direction="column"
                    style={{ borderRadius: 10, padding: 10, backgroundColor: Colors.lightTurq, gap: 16 }}
                >
                    <Grid item container direction="column">
                        <Text bold>{'1. Search for the Worker in Clearinghouse:'}</Text>
                        <Grid item container direction="row" gap={1}>
                            <Text>{'- Visit the '}</Text>
                            <a href="https://crw.flclearinghouse.com/" target="_blank" rel="noreferrer">
                                {'Clearinghouse Search Page'}
                            </a>
                        </Grid>
                    </Grid>
                    <Grid item container direction="column">
                        <Text bold>{"2. Pull up the worker's information to search for them"}</Text>
                        <Grid item container justifyContent="flex-end">
                            <SecondaryButton buttonStyle={{ width: '35%' }} onClick={() => onSendCode()}>
                                {'Reveal Information'}
                            </SecondaryButton>
                        </Grid>
                    </Grid>
                    <Grid item container>
                        <Text bold>{'3. Initiate Resubmission:'}</Text>
                        <Text>
                            {
                                "- Once you've found the worker's profile, select 'Initiate Resubmission' and use information above to complete the process."
                            }
                        </Text>
                    </Grid>
                    <Text textStyle={{ marginTop: 24 }}>
                        {
                            'Note: The resubmission fee is typically between $13-$14. Tandem will reimburse this amount on your next invoice.'
                        }
                    </Text>
                </Grid>
                <Text bold>{'Reimbursement:'}</Text>
                <Text>
                    {
                        'Tandem will credit your account for the resubmission fee on your next invoice. Please forward your receipt from Clearinghouse to team@join-tandem.com.'
                    }
                </Text>
                <Grid item container justifyContent="flex-end">
                    <PrimaryButton loading={loading} onClick={onConfirm} buttonStyle={{ width: '45%' }}>
                        {"I've completed this"}
                    </PrimaryButton>
                </Grid>
            </Grid>
        );
    };

    const AgencyReviewContent = () => {
        return (
            <Grid item container style={{ padding: 20, gap: 20 }}>
                <Text variant="h1">{`Action Required: Initiate Agency Review`}</Text>
                <Text>
                    {
                        "The worker you've booked has successfully passed their background check and met all other state requirements. However, they can't start until you complete one final step in the Clearinghouse."
                    }
                </Text>
                <Text bold>{'Please follow these steps to initiate the agency review:'}</Text>
                <Grid
                    item
                    container
                    direction="column"
                    style={{ borderRadius: 10, padding: 10, backgroundColor: Colors.lightTurq, gap: 16 }}
                >
                    <Grid item container direction="column">
                        <Text bold>{'1. Search for the Worker in Clearinghouse:'}</Text>
                        <Grid item container direction="row" gap={1}>
                            <Text>{'- Visit the '}</Text>
                            <a href="https://crw.flclearinghouse.com/" target="_blank" rel="noreferrer">
                                {'Clearinghouse Search Page'}
                            </a>
                        </Grid>
                    </Grid>
                    <Grid item container direction="column">
                        <Text bold>{"2. Pull up the worker's information to search for them"}</Text>
                        <Grid item container justifyContent="flex-end">
                            <SecondaryButton buttonStyle={{ width: '35%' }} onClick={() => onSendCode()}>
                                {'Reveal Information'}
                            </SecondaryButton>
                        </Grid>
                    </Grid>
                    <Grid item container>
                        <Text bold>{'3. Initiate Agency Review:'}</Text>
                        <Text>
                            {
                                "- Once you've found the worker's profile, select 'Initiate Agency Review' and use information above to complete the process."
                            }
                        </Text>
                    </Grid>
                </Grid>
                <Grid item container justifyContent="flex-end">
                    <PrimaryButton loading={loading} onClick={onConfirm} buttonStyle={{ width: '45%' }}>
                        {"I've completed this"}
                    </PrimaryButton>
                </Grid>
            </Grid>
        );
    };

    return (
        <SizeableRoundedDialog
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            style={{ maxWidth: 1000 }}
            closeButton
        >
            {actionText.toLowerCase().includes('resubmission') ? <ResubmissionContent /> : <AgencyReviewContent />}
            <RevealModal open={revealOpen} onClose={() => setRevealOpen(false)} provider={provider_user} />
        </SizeableRoundedDialog>
    );
}
